<template>
    <div>
        <v-card
            flat>
            <v-card-title>
                {{ $t("mileage_readings") }}
                <v-spacer></v-spacer>
                <v-btn
                    icon
                    v-if="selected.mileageReadings.length == 1"
                    @click="openMileageReadingDisplayDialog(selected.mileageReadings[0].id)">
                    <v-icon>mdi-eye</v-icon>
                </v-btn>
                <v-btn 
                    icon
                    @click="createdDataFetch">
                    <v-icon>mdi-refresh</v-icon>
                </v-btn>
                <v-menu
                    offset-y>
                    <template v-slot:activator="{on}">
                        <v-btn
                            icon
                            v-on="on">
                            <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item
                            link
                            @click="openMileageReadingDialog({editMode: false})"
                            v-if="authorizations.mileage_readings.create">
                            <v-list-item-avatar>
                                <v-icon>mdi-plus-circle</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-title>{{ $t("new") }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            link
                            v-if="selected.mileageReadings.length == 1"
                            @click="openMileageReadingDisplayDialog(selected.mileageReadings[0].id)">
                            <v-list-item-avatar>
                                <v-icon>mdi-eye</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-title>{{ $t('show') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            link
                            v-if="selected.mileageReadings.length == 1"
                            @click="openMileageReadingDialog({editMode: true, id: selected.mileageReadings[0].id})">
                            <v-list-item-avatar>
                                <v-icon>mdi-pencil</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>{{ $t("ACTIONS_MENU_EDIT") }}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item
                            link
                            v-if="selected.mileageReadings.length == 1"
                            @click="openMileageReadingDeleteDialog(selected.mileageReadings[0].id)">
                            <v-list-item-avatar>
                                <v-icon>mdi-delete</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>{{ $t("ACTIONS_MENU_DELETE") }}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-card-title>
            <v-card-text>
                <v-data-table
                    show-select
                    single-select
                    dense
                    :headers="headers.mileage"
                    :items="mileageReadings"
                    :loading="loading.data"
                    :options.sync="options"
                    v-model="selected.mileageReadings"
                    :server-items-length="totalMileageReadings">
                </v-data-table>
            </v-card-text>
        </v-card>
        <MileageReadingDialog
            ref="mileageReadingDialog"
            @submit="onMileageReadingDialogSubmit"/>
        <RetryDialog
            ref="retryDialog"
            @submit="onRetryDialogSubmit"/>
        <DecisionDialog
            ref="decisionDialog"
            @submit="onDecisionDialogSubmit"/>
        <AlertDialog
            ref="alertDialog"/>
        <MileageReadingDisplayDialog
            ref="mileageReadingDisplayDialog"/>
    </div>
</template>


<script>
import MileageReadingDialog from '../components/MileageReadingDialog';
import MileageReadingDisplayDialog from '../components/MileageReadingDisplayDialog';
import { mapState, mapActions } from 'vuex';
import RetryDialog from '../components/RetryDialog';
import DecisionDialog from '../components/DecisionDialog';
import AlertDialog from '../components/AlertDialog';

export default {
    name: "MileageReadings",
    components: {
        MileageReadingDialog,
        RetryDialog,
        DecisionDialog,
        AlertDialog,
        MileageReadingDisplayDialog
    },
    data() {
        return {
            totalMileageReadings: 0,
            loading: {
                data: false
            },
            headers: {
                mileage: [
                    { text: this.$t("date"), value: "date"},
                    { text: this.$t("truck_types"), value: "truck_type", sortable: false},
                    { text: this.$t("trucks_count"), value: "count", sortable: false},
                    { text: this.$t("total_distance"), value: "total_distance", sortable: false}
                ]
            },
            selected: {
                mileageReadings: []
            },
            options: {}
        }
    },
    computed: {
        ...mapState("authentication", ["authorizations"]),
        ...mapState("mileageReading", ["mileageReadings"])
    },
    watch: {
        options: {
            handler () {
                this.createdDataFetch(); 
            },
            deep: true
        }
    },
    methods: {
        ...mapActions("mileageReading", ["fetchMileageReadings", "removeMileageReading"]),
        openMileageReadingDialog(params) {
            this.$refs.mileageReadingDialog.open(params);
        },
        async createdDataFetch() {
            try {
                this.loading.data = true;
                let result = await this.fetchMileageReadings(this.options);
                this.totalMileageReadings = result.data.total;
                this.loading.data = false;
            } catch(error) {
                let message = error.response ? error.response.data.message : error;
                this.$refs.retryDialog.open({message})
            }
        },
        onRetryDialogSubmit(params) {
            if(params.target == "createdDataFetch") {
                if(params.retry) this.createdDataFetch();
            }
        },
        onMileageReadingDialogSubmit() {
            this.createdDataFetch();
        },
        openMileageReadingDeleteDialog(id) {
            this.$refs.decisionDialog.open({
                title: this.$t("DELETE_CONFIRM_TITLE"),
                content: this.$t("DELETE_CONFIRM_CONTENT"),
                action: "DELETE_MILEAGE_READING",
                id: id
            });
        },
        onDecisionDialogSubmit({action, id}) {
            if(action == "DELETE_MILEAGE_READING") {
                this.removeMileageReading(id)
                    .then(() => {
                        this.$refs.alertDialog.open(this.$t("SUCCESS_TITLE"), this.$t("RECORD_DELETE_SUCCESS"));
                        this.createdDataFetch();
                    })
                    .catch(error => {
                        let message = error.response ? error.response.message : error;
                        this.$refs.alertDialog.open(this.$t("ERROR_TITLE"), message);
                    })
            }
        },
        openMileageReadingDisplayDialog(id) {
            this.$refs.mileageReadingDisplayDialog.open({id});
        }
    }
}
</script>